import { useState, useEffect } from 'react'
import { Organisation } from '../../services/api'
import { api } from '../../services'
import DomainVerificationModal from './DomainVerificationModal'

interface OrganisationSettingsProps {
  organisation: Organisation
  onUpdate?: () => void
}

const OrganisationSettings: React.FC<OrganisationSettingsProps> = ({ 
  organisation: initialOrganisation,
  onUpdate 
}) => {
  const [showVerificationModal, setShowVerificationModal] = useState(false)
  const [organisation, setOrganisation] = useState(initialOrganisation)
  const [isLoading, setIsLoading] = useState(false)

  // Fetch full organisation details including verification token
  useEffect(() => {
    const fetchOrganisation = async () => {
      const orgId = initialOrganisation.organisation_id || initialOrganisation.id
      if (!orgId) return

      setIsLoading(true)
      try {
        const fullOrg = await api.organisations.get(orgId)
        setOrganisation(fullOrg)
      } catch (error) {
        console.error('Failed to fetch organisation details:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchOrganisation()
  }, [initialOrganisation])

  const handleVerified = async () => {
    setShowVerificationModal(false)
    
    // Refresh organisation data
    const orgId = organisation.organisation_id || organisation.id
    if (orgId) {
      try {
        const fullOrg = await api.organisations.get(orgId)
        setOrganisation(fullOrg)
      } catch (error) {
        console.error('Failed to refresh organisation:', error)
      }
    }
    
    onUpdate?.()
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500 dark:text-gray-400">Loading organisation details...</div>
      </div>
    )
  }

  return (
    <div>
      <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
        Organisation Settings
      </h2>

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Organisation Name
          </label>
          <input
            type="text"
            value={organisation.organisation_display_name}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
            readOnly
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Target Domain
          </label>
          <div className="flex items-center space-x-2">
            <input
              type="text"
              value={organisation.target_domain}
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
              readOnly
            />
            {organisation.domain_verified ? (
              <span className="inline-flex items-center px-3 py-2 text-sm font-medium text-green-700 dark:text-green-200 bg-green-100 dark:bg-green-900 rounded">
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                Verified
              </span>
            ) : (
              <button
                onClick={() => setShowVerificationModal(true)}
                className="px-3 py-2 text-sm font-medium text-yellow-700 dark:text-yellow-200 bg-yellow-100 dark:bg-yellow-900 rounded hover:bg-yellow-200 dark:hover:bg-yellow-800 transition-colors"
              >
                Verify Domain
              </button>
            )}
          </div>
          {!organisation.domain_verified && (
            <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">
              Domain verification is required to send campaigns to your organization.
            </p>
          )}
        </div>

        <div>
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Organisation Settings
          </h3>
          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                className="rounded border-gray-300 dark:border-gray-600 text-blue-600 dark:text-blue-400 focus:ring-blue-500 dark:focus:ring-blue-400 dark:bg-gray-700"
                defaultChecked
              />
              <span className="ml-2 text-sm text-gray-600 dark:text-gray-300">
                Send campaign notifications to administrators
              </span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                className="rounded border-gray-300 dark:border-gray-600 text-blue-600 dark:text-blue-400 focus:ring-blue-500 dark:focus:ring-blue-400 dark:bg-gray-700"
                defaultChecked
              />
              <span className="ml-2 text-sm text-gray-600 dark:text-gray-300">
                Require approval for campaigns over 50 targets
              </span>
            </label>
          </div>
        </div>

        <div className="pt-4 border-t border-gray-200 dark:border-gray-600">
          <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded transition-colors">
            Save Changes
          </button>
        </div>
      </div>

      {/* Domain Verification Modal */}
      {showVerificationModal && (
        <DomainVerificationModal
          organisation={organisation}
          onClose={() => setShowVerificationModal(false)}
          onVerified={handleVerified}
        />
      )}
    </div>
  )
}

export default OrganisationSettings
